import { Avatar, Flex } from "antd";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UserShortWithWork } from "tm-shared/types";

import { useAppDispatch } from "@/hooks/redux";
import { useLoadUserSchedule } from "@/pages/SV/Team/hooks/useLoadUserSchedule";
import { clearSVActiveSchedule } from "@/store/slices/manager";
import { addModalToQueue } from "@/store/slices/ui";
import { ModalType } from "@/types/modals";

import { SIZES } from "../../constants";
import { useScheduleStatus } from "../../hooks/useScheduleStatus";
import {
    StyledMemberCard,
    StyledTeamBadge,
    StyledTeamLogo,
} from "../../Styled";
import { MemberActions } from "../MemberActions";
import { MemberInfo } from "../MemberInfo";

interface MemberCardProps {
    member: UserShortWithWork;
    routeId?: string;
    isActive: boolean;
    isProcessing: boolean;
    onLoadSchedule: ReturnType<
        typeof useLoadUserSchedule
    >["handleLoadUserSchedule"];
    onNavigate: () => void;
}

export const MemberCard = ({
    member,
    isActive,
    isProcessing,
    onLoadSchedule,
    onNavigate,
    routeId,
}: MemberCardProps) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const memberUserId = member.userId ?? "";
    const { hasSchedule, canAccess } = useScheduleStatus(
        memberUserId,
        isActive,
    );

    const handleLoad = () => {
        onLoadSchedule(memberUserId, member.role, routeId ?? "");
    };

    const handleDashboardNavigate = () => {
        navigate(`/team/${member.userId}`);
    };

    const handleDeleteActiveSchedule = () => {
        dispatch(clearSVActiveSchedule());
    };

    const createCustomTaskForRoute = useCallback(async () => {
        dispatch(
            addModalToQueue({
                type: ModalType.CreateCustomTask,
                id: ModalType.CreateCustomTask,
                payload: {
                    agentId: memberUserId,
                    images: [],
                    routeId,
                },
            }),
        );
    }, [dispatch, memberUserId, routeId]);

    return (
        <StyledMemberCard $isActive={isActive}>
            <Flex justify="flex-start" gap="12px" align="center">
                <StyledTeamBadge dot $isWorking={member.isWorking}>
                    <Avatar size={SIZES.AVATAR} icon={<StyledTeamLogo />} />
                </StyledTeamBadge>

                <MemberInfo member={member} />
            </Flex>

            <MemberActions
                isProcessing={isProcessing}
                hasSchedule={hasSchedule}
                canAccess={canAccess}
                onLoad={handleLoad}
                onNavigate={onNavigate}
                onDashboard={handleDashboardNavigate}
                onDelete={handleDeleteActiveSchedule}
                onCreateCustomTask={createCustomTaskForRoute}
            />
        </StyledMemberCard>
    );
};
